import { createContext, useContext, useEffect, useMemo } from 'react';
import Cookies from 'js-cookie';
import AppConfig from '@/AppConfig';
import {
  JWTPayload,
  decodeAuthToken,
  redirectToPlumApp,
  track,
  verifyTokenAndInit,
} from '@/utils';
import safeJsonParse from '@/utils/safeJsonParse';
import {
  EMessageType,
  IHomepageQueryResponse,
  IMessagePayload,
  IUserConfig,
} from '@/types';
import { useQuery } from '@tanstack/react-query';
import { authenticateMeQuery } from '@/queries/auth.queries';
import { FEATURE_FLAGS } from '@/constants';
import { homePageQuery } from '@/queries/helpline.queries';

const AppContext = createContext<{
  isWebView: boolean;
  xPlumToken: string | null;
  decodedToken: JWTPayload | null;
  isAppLoading: boolean;
  userConfig: IUserConfig | null;
  startRecording: () => void;
  stopRecording: () => void;
  homepageData?: IHomepageQueryResponse;
}>({
  isWebView: false,
  xPlumToken: null,
  decodedToken: null,
  isAppLoading: false,
  userConfig: null,
  startRecording: () => {},
  stopRecording: () => {},
});

const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const searchParams = new URLSearchParams(window.location.search);
  const isWebViewParam = searchParams.get('webview') === 'true';

  const startRecording = () => {
    if (typeof window.smartlook === 'function') {
      window.smartlook('recording', 'true', {}); // Start recording
    }
  };

  const stopRecording = () => {
    if (typeof window.smartlook === 'function') {
      window.smartlook('recording', 'false', {}); // Stop recording
    }
  };

  useEffect(() => {
    if (isWebViewParam) {
      Cookies.set('isWebView', 'true');
    }
  }, [isWebViewParam]);

  const isWebView = useMemo(() => {
    return isWebViewParam || Cookies.get('isWebView') === 'true';
  }, [isWebViewParam]);

  const xPlumToken = useMemo(() => {
    return Cookies.get(AppConfig.JWT_TOKEN_COOKIE_NAME) ?? null;
  }, []);

  const { isLoading, isFetched, data } = useQuery({
    queryKey: authenticateMeQuery().queryKey,
    queryFn: authenticateMeQuery().queryFn,
    refetchInterval: 1000 * 60 * 15, // 15 minutes,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
  });

  useEffect(() => {
    window.dev.i('AppProvider', 'isFetched', { isFetched, xPlumToken });
    if (isFetched && !xPlumToken) {
      const redirectToUrl = encodeURI(window.location.href);
      redirectToPlumApp(`/logout?redirectTo=${redirectToUrl}`)();
    }
  }, [isFetched, xPlumToken]);

  useEffect(() => {
    const decodedToken = decodeAuthToken(xPlumToken ?? '');
    if (decodedToken) {
      const handleAnalyticsLoaded = () => {
        track.identifyUserToAnalytics(decodedToken);
      };

      window.addEventListener(
        AppConfig.ANALYTICS_LOADED_EVENT,
        handleAnalyticsLoaded,
      );

      return () => {
        window.removeEventListener(
          AppConfig.ANALYTICS_LOADED_EVENT,
          handleAnalyticsLoaded,
        );
      };
    }
  }, [xPlumToken]);

  /**
   * This useEffect listens for messages from the parent window
   * and verifies the token and initializes the app
   */
  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      const { data: message, origin } = event;

      if (import.meta.env.PROD && origin === AppConfig.PLUM_APP_HOST_URL) {
        const payload = safeJsonParse(message) as IMessagePayload;

        if (payload == null) {
          return;
        }

        if (payload?.type === EMessageType.TOKEN) {
          verifyTokenAndInit(String(payload?.data));
        }
      }
    };

    window.addEventListener('message', messageHandler);

    // Send ready message to parent window
    if (window.parent !== window) {
      window.parent.postMessage(
        JSON.stringify({
          type: EMessageType.READY,
          data: [EMessageType.TOKEN],
        }),
        AppConfig.PLUM_APP_HOST_URL,
      );
    }

    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, []);

  const isAppLoading = isLoading || !isFetched;

  useEffect(() => {
    if (data?.featureFlags) {
      const { featureFlags } = data;
      const isNewCashlessEnabled =
        featureFlags?.find?.((feat) => {
          return feat.name === FEATURE_FLAGS.NEW_CASHLESS_CREATE;
        })?.enabled ?? false;

      const isNetworkHospitalsEnabled =
        featureFlags?.find?.((feat) => {
          return feat.name === FEATURE_FLAGS.NETWORK_HOSPITALS_V3;
        })?.enabled ?? false;

      const isNHRoute = window.location.pathname === '/network-hospitals';
      const isCreateRoute = window.location.pathname === '/claims/create';

      if (
        (isNHRoute && !isNetworkHospitalsEnabled) ||
        (isCreateRoute && !isNewCashlessEnabled)
      ) {
        redirectToPlumApp('/claims')();
      }
    }
  }, [data]);

  useEffect(() => {
    if (data?.id && window?.smartlook) {
      window?.smartlook?.('init', AppConfig.SMARTLOOK_KEY, {
        region: 'eu',
      });
      window?.smartlook('identify', data?.id as string, {
        memberId: data?.id,
        organisationId: data?.organisation?.id,
        email: data?.email,
      });
    }
  }, [data]);

  const { data: homepageData } = useQuery(
    homePageQuery({ xPlumToken: xPlumToken ?? '' }),
  );

  const values = useMemo(() => {
    const decodedToken = decodeAuthToken(xPlumToken ?? '');

    return {
      isWebView,
      xPlumToken,
      decodedToken,
      isAppLoading,
      userConfig: data as IUserConfig,
      startRecording,
      stopRecording,
      homepageData,
    };
  }, [isWebView, xPlumToken, data, isAppLoading, homepageData]);

  return <AppContext.Provider value={values}>{children}</AppContext.Provider>;
};

const useAppContext = () => {
  const values = useContext(AppContext);
  if (!values) {
    throw new Error('App must be wrapped in AppProvider');
  }
  return values;
};

export { AppContext, useAppContext };
export default AppProvider;
